import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  announcementDetailsWithRecipients,
  selectedAnnouncementsTabCountFragment,
} from "./AnnouncementFragments";
import {
  getAnnouncementDetailsQuery,
  getAnnouncementPublishersQuery,
  getAnnouncementsTabsCountQuery,
  getAnnouncementsFeedQuery,
  getOrganizationGradesQuery,
  getUserCoursesQuery,
  getOrganizationCoursesQuery,
  getUserCountByEntityCourseWithEntityIds,
  getUnreadAndNewAnnouncementsCountQuery,
  getSelectedTabAnnouncementsCountQuery,
  getAnnouncementCoursesV2Query,
  getOrganizationCoursesCountQuery,
  getAnnouncementRecipientsQuery,
  getUserCoursesV2Query,
  getDefaultBannerImageQuery,
  getCourseUnreadAnnouncementsCountQuery,
  getLearningCourseUnreadAnnouncementsCountQuery,
  getUserLearningCourseClassesQuery,
} from "./AnnouncementQuery";

export const getAnnouncementPublishersFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getAnnouncementPublishersQuery,
      variables,
    });
    return _.get(queryData, "node.circularPublishers", []);
  } catch (err) {
    return [];
  }
};

export const getSelectedTabAnnouncementsCountFromCache = ({ variables }) => {
  try {
    return client.readQuery({
      query: getSelectedTabAnnouncementsCountQuery,
      variables,
    });
  } catch (err) {
    return {};
  }
};

export const readSelectedTabCountFragment = ({ organizationId, variables }) => {
  try {
    return client.readFragment({
      id: `Organization:${organizationId}`,
      fragment: selectedAnnouncementsTabCountFragment,
      fragmentName: "selectedAnnouncementsTabCountItem",
      variables,
    });
  } catch (err) {
    return {};
  }
};

export const writeToSelectedTabCountFragment = ({
  variables,
  organizationId,
  data,
}) => {
  try {
    client.writeFragment({
      id: `Organization:${organizationId}`,
      fragment: selectedAnnouncementsTabCountFragment,
      fragmentName: "selectedAnnouncementsTabCountItem",
      variables,
      data,
    });
  } catch (err) {}
};

export const getAnnouncementFeedFromCache = ({
  variables,
  returnFullData = false,
}) => {
  try {
    const queryData = client.readQuery({
      query: getAnnouncementsFeedQuery,
      variables,
    });
    return returnFullData ? queryData : _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const writeToAnnouncementFeedInCache = ({ variables, data }) => {
  try {
    client.writeQuery({
      query: getAnnouncementsFeedQuery,
      variables,
      data,
    });
  } catch (err) {}
};

export const getAnnouncementDetailsFromCache = ({ variables }) => {
  try {
    return client.readQuery({
      query: getAnnouncementDetailsQuery,
      variables,
    });
  } catch (err) {
    return {};
  }
};

export const getUnreadAndNewAnnouncementsCountFromCache = ({ variables }) => {
  try {
    return client.readQuery({
      query: getUnreadAndNewAnnouncementsCountQuery,
      variables,
    });
  } catch (err) {
    return {};
  }
};

export const readAnnouncementTabsCountFromCache = ({ variables }) => {
  try {
    return client.readQuery({
      query: getAnnouncementsTabsCountQuery,
      variables,
    });
  } catch (err) {
    return {};
  }
};

export const readAnnouncementFromCache = ({
  announcementId,
  isRecipientsVisible,
  academicYearId,
}) => {
  try {
    return client.readFragment({
      id: `Circular:${announcementId}`,
      fragment: announcementDetailsWithRecipients,
      fragmentName: "announcementDetailsItem",
      variables: { isRecipientsVisible, academicYearId },
    });
  } catch (err) {
    return {};
  }
};

export const writeAnnouncementToCache = ({
  announcementId,
  isRecipientsVisible,
  academicYearId,
  data,
}) => {
  try {
    return client.writeFragment({
      id: `Circular:${announcementId}`,
      fragment: announcementDetailsWithRecipients,
      fragmentName: "announcementDetailsItem",
      variables: { isRecipientsVisible, academicYearId },
      data,
    });
  } catch (err) {
    return {};
  }
};

export const readAnnouncementRecipients = ({
  announcementId,
  academicYearId,
}) => {
  try {
    return client.readQuery({
      query: getAnnouncementRecipientsQuery,
      variables: { announcementId, academicYearId },
    });
  } catch (err) {
    return {};
  }
};

export const readAnnouncementCoursesV2FromCache = ({
  announcementId,
  academicYearId,
}) => {
  try {
    return client.readQuery({
      query: getAnnouncementCoursesV2Query,
      variables: { announcementId, academicYearId },
    });
  } catch (err) {
    return {};
  }
};

export const getUserCoursesFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getUserCoursesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getUserCoursesV2FromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getUserCoursesV2Query,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getUserCountByEntityCourseWithEntityIdsFromCache = ({
  variables,
}) => {
  try {
    return client.readQuery({
      query: getUserCountByEntityCourseWithEntityIds,
      variables,
    });
  } catch (e) {
    return {};
  }
};

export const getOrganizationCoursesFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationCoursesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getOrganizationGradesFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationGradesQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getOrganizationCoursesCountFromCache = ({
  organizationId,
  filters,
}) => {
  try {
    const queryData = client.readQuery({
      query: getOrganizationCoursesCountQuery,
      variables: {
        organizationId,
        filters,
      },
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getDefaultBannerFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getDefaultBannerImageQuery,
      variables,
    });
    const node = _.get(queryData, "node", {});
    const defaultBannerImages = _.get(node, "globalConstants", []);
    const defaultBannerImagesParsed = _.map(defaultBannerImages, image => {
      return {
        ...(_.isNil(image.value) ? {} : JSON.parse(image.value)),
      };
    });
    return defaultBannerImagesParsed;
  } catch (e) {
    return null;
  }
};

export const getUserLearningCourseClassesFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getUserLearningCourseClassesQuery,
      variables,
    });
    return _.map(
      _.get(queryData, "node.coursesV2.edges", []),
      ({ node }) => node
    );
  } catch (e) {
    return [];
  }
};

export const getCourseUnreadAnnouncementsCountFromCache = ({ variables }) => {
  try {
    const queryData = client.readQuery({
      query: getCourseUnreadAnnouncementsCountQuery,
      variables,
    });
    return _.get(queryData, "node.circularFeedV3.unreadAnnouncementsCount", 0);
  } catch (e) {
    return 0;
  }
};

export const writeCourseUnreadAnnouncementsCountToCache = ({
  variables,
  data,
}) => {
  try {
    client.writeQuery({
      query: getCourseUnreadAnnouncementsCountQuery,
      variables,
      data: { node: data },
    });
  } catch (err) {}
};

export const getLearningCourseUnreadAnnouncementsCountFromCache = ({
  variables,
}) => {
  try {
    const queryData = client.readQuery({
      query: getLearningCourseUnreadAnnouncementsCountQuery,
      variables,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return 0;
  }
};

export const writeLearningCourseUnreadAnnouncementsCountToCache = ({
  variables,
  data,
}) => {
  try {
    client.writeQuery({
      query: getLearningCourseUnreadAnnouncementsCountQuery,
      variables,
      data: { node: data },
    });
  } catch (err) {}
};
